import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSport } from '../context/SportContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { KeyRound, Loader2, ArrowLeft, Trophy, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function CustomSportEditor({ api, sport, refresh }) {
  const isCustom = sport.sport === 'custom';
  const [positions, setPositions] = useState(sport.positions || []);
  const [sessionTypes, setSessionTypes] = useState(
    (sport.sessionTypes || sport.session_types || []).map(s => ({ ...s }))
  );
  const [posInput, setPosInput] = useState('');
  const [stInput, setStInput] = useState({ label: '', value: '', icon: 'Activity' });
  const [saving, setSaving] = useState(false);

  const addPosition = () => {
    const v = posInput.trim();
    if (!v) return;
    if (positions.includes(v)) return toast.error('Already added');
    setPositions([...positions, v]);
    setPosInput('');
  };
  const removePosition = (p) => setPositions(positions.filter(x => x !== p));

  const addSessionType = () => {
    const label = stInput.label.trim();
    if (!label) return;
    const value = (stInput.value || label).trim().toUpperCase().replace(/\s+/g, '_');
    if (sessionTypes.some(s => s.value === value)) return toast.error('Value already used');
    setSessionTypes([...sessionTypes, { value, label, icon: stInput.icon || 'Activity', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' }]);
    setStInput({ label: '', value: '', icon: 'Activity' });
  };
  const removeSessionType = (v) => setSessionTypes(sessionTypes.filter(s => s.value !== v));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/sport-config/custom', { positions, session_types: sessionTypes });
      toast.success('Custom configuration saved');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isCustom) {
    return (
      <div className="text-xs text-zinc-500 leading-relaxed">
        This organisation uses the <span className="text-amber-400 font-semibold">{sport.sportLabel}</span> defaults. Positions, planner session types and icons are managed by the platform. To customise these, ask SuperAdmin to switch the sport to <span className="text-zinc-300">Custom</span>.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs text-zinc-400 mb-2">Athlete positions ({positions.length})</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {positions.map(p => (
            <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800 text-xs text-zinc-200" data-testid={`custom-position-${p}`}>
              {p}
              <button type="button" onClick={() => removePosition(p)} className="text-zinc-500 hover:text-rose-400" aria-label={`Remove ${p}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          {positions.length === 0 && <span className="text-[11px] text-zinc-600">None yet — add some below.</span>}
        </div>
        <div className="flex gap-2">
          <Input
            value={posInput}
            onChange={e => setPosInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPosition(); } }}
            placeholder="e.g. Goalkeeper"
            className="bg-zinc-950 border-zinc-800 text-sm"
            data-testid="custom-position-input"
          />
          <Button type="button" size="sm" onClick={addPosition} data-testid="custom-position-add"><Plus className="w-4 h-4" /></Button>
        </div>
      </div>

      <div>
        <div className="text-xs text-zinc-400 mb-2">Planner session types ({sessionTypes.length})</div>
        <div className="space-y-1.5 mb-2">
          {sessionTypes.map(s => (
            <div key={s.value} className="flex items-center gap-2 text-xs bg-zinc-800/40 rounded px-2 py-1" data-testid={`custom-session-${s.value}`}>
              <span className="font-mono text-amber-400 w-32 truncate">{s.value}</span>
              <span className="flex-1 text-zinc-200">{s.label}</span>
              <span className="text-zinc-500">{s.icon}</span>
              <button type="button" onClick={() => removeSessionType(s.value)} className="text-zinc-500 hover:text-rose-400" aria-label="Remove"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {sessionTypes.length === 0 && <span className="text-[11px] text-zinc-600">None yet — add some below.</span>}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input value={stInput.label} onChange={e => setStInput({ ...stInput, label: e.target.value })} placeholder="Label (e.g. Match)" className="bg-zinc-950 border-zinc-800 text-sm" data-testid="custom-session-label" />
          <Input value={stInput.value} onChange={e => setStInput({ ...stInput, value: e.target.value })} placeholder="Code (optional)" className="bg-zinc-950 border-zinc-800 text-sm" data-testid="custom-session-value" />
          <div className="flex gap-1">
            <Input value={stInput.icon} onChange={e => setStInput({ ...stInput, icon: e.target.value })} placeholder="Icon (Lucide)" className="bg-zinc-950 border-zinc-800 text-sm" data-testid="custom-session-icon" />
            <Button type="button" size="sm" onClick={addSessionType} data-testid="custom-session-add"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Icon: any Lucide name (Activity, Heart, Swords, Dumbbell, Trophy, Gamepad2, ...). Defaults to Activity.</p>
      </div>

      <Button onClick={save} disabled={saving} className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="custom-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Custom Configuration'}
      </Button>
    </div>
  );
}

export default function SettingsPage() {
  const { api, user } = useAuth();
  const sport = useSport();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  const handleChange = async () => {
    if (!current) return toast.error('Enter current password');
    if (newPwd.length < 6) return toast.error('New password must be at least 6 characters');
    if (newPwd !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.post('/users/change-password', { current_password: current, new_password: newPwd });
      toast.success('Password changed successfully');
      setCurrent(''); setNewPwd(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-bold">{t('Settings', 'Impostazioni')}</h1>
        </div>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader><CardTitle className="text-sm text-zinc-400">{t('Account', 'Account')}</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">{t('Email', 'Email')}</span><span className="text-zinc-200">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">{t('Role', 'Ruolo')}</span><span className="text-zinc-200">{user?.role}</span></div>
            {user?.organization_name && (
              <div className="flex justify-between"><span className="text-zinc-500">{t('Organization', 'Organizzazione')}</span><span className="text-zinc-200" data-testid="settings-org-name">{user.organization_name}</span></div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="sport-config-card">
          <CardHeader>
            <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
              <Trophy className="w-4 h-4" /> {t('Sport Configuration', 'Configurazione Sport')}
              <span className="ml-auto text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid="current-sport-label">{sport.sportLabel}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {isAdmin ? (
              <CustomSportEditor api={api} sport={sport} refresh={sport.refresh} />
            ) : (
              <div className="text-xs text-zinc-500 leading-relaxed">
                {t('Your organisation uses the', 'La tua organizzazione utilizza la configurazione')} <span className="text-amber-400 font-semibold">{sport.sportLabel}</span> {t('configuration. Positions and planner session types are managed by your admin.', '. Posizioni e tipi di sessione sono gestiti dal tuo admin.')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> {t('Change Password', 'Cambia Password')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input type="password" placeholder={t('Current password', 'Password attuale')} value={current} onChange={e => setCurrent(e.target.value)} className="bg-zinc-950 border-zinc-800" data-testid="current-password" />
            <Input type="password" placeholder={t('New password', 'Nuova password')} value={newPwd} onChange={e => setNewPwd(e.target.value)} className="bg-zinc-950 border-zinc-800" data-testid="new-password" />
            <Input type="password" placeholder={t('Confirm new password', 'Conferma nuova password')} value={confirm} onChange={e => setConfirm(e.target.value)} className="bg-zinc-950 border-zinc-800" data-testid="confirm-password" />
            <Button onClick={handleChange} disabled={loading} className="w-full" data-testid="change-password-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Change Password', 'Cambia Password')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
